"use client";

import type React from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  VStack,
  Box,
  Text,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Image,
  Link,
  Code,
  HStack,
  useClipboard,
  IconButton,
} from "@chakra-ui/react";
import { CopyIcon, CheckIcon, ExternalLinkIcon } from "@chakra-ui/icons";

interface RetellWebhookDialogProps {
  isOpen: boolean;
  onClose: () => void;
  applicationUrl: string;
  onComplete: () => void;
  isLoading?: boolean;
}

const RetellWebhookDialog: React.FC<RetellWebhookDialogProps> = ({
  isOpen,
  onClose,
  applicationUrl,
  onComplete,
  isLoading = false,
}) => {
  const webhookUrl = `https://${applicationUrl}/calls/webhook`;
  const { hasCopied, onCopy } = useClipboard(webhookUrl);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="xl"
      closeOnOverlayClick={false}
    >
      <ModalOverlay />
      <ModalContent maxW="900px" maxH="90vh">
        <ModalHeader>Configure Retell AI Webhook</ModalHeader>
        <ModalCloseButton />
        <ModalBody overflowY="auto" maxH="calc(90vh - 140px)">
          <VStack spacing={4} align="stretch">
            <Alert status="info" borderRadius="md">
              <AlertIcon />
              <Box flex="1">
                <AlertTitle fontSize="md">
                  Webhook Configuration Required
                </AlertTitle>
                <AlertDescription fontSize="sm">
                  To complete your setup, you need to configure the
                  account-level webhook URL in your Retell AI dashboard. Make
                  sure to use the same account/workspace from which you copied
                  your API key.
                </AlertDescription>
              </Box>
            </Alert>

            <Box>
              <Text fontWeight="bold" mb={3}>
                Step 1: Copy your webhook URL
              </Text>
              <HStack spacing={2} bg="gray.50" p={3} borderRadius="md">
                <Code fontSize="sm" wordBreak="break-all" flex="1">
                  {webhookUrl}
                </Code>
                <IconButton
                  aria-label="Copy webhook URL"
                  icon={hasCopied ? <CheckIcon /> : <CopyIcon />}
                  size="sm"
                  onClick={onCopy}
                  colorScheme={hasCopied ? "green" : "blue"}
                />
              </HStack>
            </Box>

            <Box>
              <Text fontWeight="bold" mb={2}>
                Step 2: Configure in Retell AI Dashboard
              </Text>
              <Text fontSize="sm" mb={3}>
                Go to{" "}
                <Link
                  href="https://dashboard.retellai.com/settings/webhooks"
                  isExternal
                  color="blue.500"
                  fontWeight="medium"
                >
                  Retell AI Webhooks Settings <ExternalLinkIcon mx="2px" />
                </Link>{" "}
                and paste the webhook URL above like it's done in the screenshot
                below.
              </Text>

              <Box
                borderRadius="md"
                overflow="hidden"
                border="2px solid"
                borderColor="gray.200"
              >
                <Image
                  src="/retell-webhook-preview.png"
                  alt="Retell AI Webhook Configuration"
                  width="100%"
                />
              </Box>
            </Box>

            <Alert status="warning" borderRadius="md">
              <AlertIcon />
              <Box flex="1">
                <AlertDescription fontSize="sm">
                  <strong>Important:</strong> Make sure to set the{" "}
                  <strong>Webhook Timeout</strong> to{" "}
                  <strong>30 seconds</strong> as shown in the screenshot above.
                  This ensures your webhook has enough time to process calls.
                </AlertDescription>
              </Box>
            </Alert>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            I'll do this later
          </Button>
          <Button
            colorScheme="blue"
            onClick={onComplete}
            isLoading={isLoading}
            loadingText="Setting up..."
          >
            I've added the webhook URL
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default RetellWebhookDialog;
